import { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../@/components/ui/dialog';
import { Button } from '../../@/components/ui/button';
import { Label } from '../../@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../@/components/ui/select';
import { Info } from 'lucide-react';


// Student Selector Dialog Component
export default function StudentSelectorDialog({
  open,
  onOpenChange,
  cohorts,
  allCourses,
  allTms,
  selectedCohorts = [],
  setSelectedCohorts,
  selectedCourse,
  setSelectedCourse,
  selectedTm,
  onSelectTm = () => { },
  populateBy = "cohort",
  populateOptions = [{ value: "cohort", label: "Cohort" }],
  onPopulateByChange,
  onAutoPopulate
}) {
  const selectedCohortId = useMemo(
    () => (Array.isArray(selectedCohorts) && selectedCohorts.length ? String(selectedCohorts[0]) : ""),
    [selectedCohorts]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md h-auto">
        <DialogHeader>
          <DialogTitle>Auto-Populate Attendees</DialogTitle>
          <p className="text-sm text-(--neutral-600) font-body">
            Auto-populate by cohort, pathway, etc.
          </p>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="populateBy" className="font-heading mb-2 text-(--neutral-900)">
              Populate By*
            </Label>
            <Select
              value={populateBy}
              onValueChange={onPopulateByChange}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Cohort" />
              </SelectTrigger>
              <SelectContent>
                {populateOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {populateBy === "cohort" && (
            <div>
              <Label htmlFor="cohort" className="font-heading mb-2 text-(--neutral-900)">
                Cohort
                 <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                <Info className="w-3 h-3" />
                Optional
              </div>
              </Label>
              <Select
                value={selectedCohortId}
                onValueChange={(value) => {
                  if (!setSelectedCohorts) return;
                  if (value === "__CLEAR_COHORT__") {
                    setSelectedCohorts([]);
                    return;
                  }
                  setSelectedCohorts(value ? [String(value)] : []);
                }}
                disabled={cohorts.length === 0}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={
                      cohorts.length === 0
                        ? "No cohorts available"
                        : "Select cohort"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__CLEAR_COHORT__">Clear cohort</SelectItem>
                  {cohorts.map((cohort) => (
                    <SelectItem key={cohort.id} value={String(cohort.id)}>
                      {cohort.name || `Cohort ${cohort.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label htmlFor="course" className="font-heading mb-2 text-(--neutral-900)">
              Filter by Course(s)
              <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                <Info className="w-3 h-3" />
                Optional
              </div>
            </Label>
            <Select value={selectedCourse} onValueChange={setSelectedCourse}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select Course" />
              </SelectTrigger>
              <SelectContent>
                {allCourses.map((course) => (
                  <SelectItem key={course.id} value={String(course.id)}>
                    {course.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="tm" className="font-heading mb-1 text-(--neutral-900)">
              Technical Mentor
              <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                <Info className="w-3 h-3" />
                Optional
              </div>
            </Label>
            <Select
              value={selectedTm}
              onValueChange={(value) => {
                if (value === "__CLEAR_TM__") {
                  onSelectTm?.("");
                  return;
                }
                onSelectTm?.(value);
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select TM" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__CLEAR_TM__">Clear TM</SelectItem>
                {allTms.map((tm) => (
                  <SelectItem key={tm.id} value={String(tm.id)}>
                    {tm.first_name} {tm.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={() => onAutoPopulate?.(populateBy)}
            variant=""
            className=""
            disabled={populateBy === "cohort" && !selectedCohortId}
          >
            Auto-Populate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}